/**
 * Reading progress for blog article body (targets [data-rr-article-reading-root]).
 * Uses requestAnimationFrame coalescing, ResizeObserver, and MutationObserver for dynamic DOM.
 */

class RrArticleReadingProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #articleRoot = null;

  /** @type {number} */
  #rafId = 0;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {(() => void) | null} */
  #onScroll = null;

  /** @type {(() => void) | null} */
  #onResize = null;

  #findArticleRoot() {
    const selectors = [
      '[data-rr-article-reading-root]',
      '.shopdev-blog-sticky-sidebar .blog-content .not-prose',
      '.shopdev-blog-sticky-sidebar .blog-content .prose',
      '.shopdev-blog-sticky-sidebar .blog-content',
      'article.blog-content',
      '.blog-post-content',
    ];

    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el instanceof HTMLElement) return el;
    }

    return null;
  }

  connectedCallback() {
    const bars = Array.from(document.querySelectorAll('rr-article-reading-progress'));
    const activeBar = bars[bars.length - 1];

    if (activeBar && activeBar !== this) {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('data-rr-disabled', 'true');
      return;
    }

    this.#articleRoot = this.#findArticleRoot();

    const hideUntilScroll = this.dataset.hideUntilScroll === 'true';
    const thresholdPx = Number.parseInt(this.dataset.scrollRevealPx || '8', 10);

    if (!this.#articleRoot) {
      this.hidden = true;
      return;
    }

    const fill = this.querySelector('[data-rr-reading-fill]');
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (fill && !prefersReducedMotion) {
      fill.classList.add('rr-article-reading-progress__fill--smooth');
    }

    const computeProgress = () => {
      const root = this.#articleRoot;
      if (!root) return 0;

      const rect = root.getBoundingClientRect();
      const scrollY = window.scrollY;
      const viewH = window.innerHeight;
      const elHeight = root.offsetHeight;
      const elTop = scrollY + rect.top;
      const elBottom = elTop + elHeight;

      if (elHeight <= viewH) {
        if (scrollY + viewH >= elBottom) return 1;
        if (scrollY + viewH <= elTop) return 0;
        return (scrollY + viewH - elTop) / elHeight;
      }

      const total = elHeight - viewH;
      const scrolled = scrollY - elTop;
      return Math.min(1, Math.max(0, scrolled / total));
    };

    const update = () => {
      this.#rafId = 0;

      const progress = computeProgress();
      const pct = Math.round(progress * 100);

      this.style.setProperty('--rr-read-progress', String(progress));
      this.style.setProperty('--rr-read-progress-pct', String(pct));

      this.setAttribute('aria-valuenow', String(pct));

      const scrollY = window.scrollY;
      const reveal = !hideUntilScroll || scrollY > thresholdPx || progress > 0.001;
      this.classList.toggle('rr-article-reading-progress--concealed', !reveal);

      if (reveal) {
        this.removeAttribute('aria-hidden');
      } else {
        this.setAttribute('aria-hidden', 'true');
      }
    };

    const schedule = () => {
      if (this.#rafId) return;
      this.#rafId = requestAnimationFrame(update);
    };

    this.#onScroll = schedule;
    this.#onResize = schedule;

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.#resizeObserver = new ResizeObserver(schedule);
    this.#resizeObserver.observe(this.#articleRoot);

    this.#mutationObserver = new MutationObserver(schedule);
    this.#mutationObserver.observe(this.#articleRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    update();
  }

  disconnectedCallback() {
    if (this.#onScroll) {
      window.removeEventListener('scroll', this.#onScroll);
    }
    if (this.#onResize) {
      window.removeEventListener('resize', this.#onResize);
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
    }
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
    }
  }
}

if (!customElements.get('rr-article-reading-progress')) {
  customElements.define('rr-article-reading-progress', RrArticleReadingProgress);
}
